import styles from "../homedashboard.module.css";

import React, { useRef, useState, useEffect, useImperativeHandle, use, Ref } from 'react';
import { IconPicker } from "components/layout/icons/IconPicker";
import { useNavigate } from "react-router";
import PilotApiRepository from "repositories/api/PilotApiRepository";
import PilotAssignmentIdbRepository from "repositories/idb/PilotAssignmentIdbRepository";
import { isArrayEmpty } from "components/helpers/ObjectHelpers";
import { getPilotAssignmentStatusColor, getPilotAssignmentUrl, isPilotAssignmentCompleted, isPilotAssignmentTentative } from "../../../pilot/pilot-assignment/helpers/PilotAssignmentHelpers";
import moment from "moment/moment";
import { ModuleOnline } from "components/module/ModuleOnline";
import ModuleDefault from "components/module/ModuleDefault";
import { ModuleWithData } from "components/module/ModuleWithData";
import { ModuleProps } from "components/props/ModuleProps"
import { createPilotPilotagesUrl } from "components/helpers/CreateUrlHelpers";
import { AppStore } from "components/stores/AppStore";
import { useStoreSelector } from "components/hooks/useStoreSelector";

interface PromiseProps {
    assignments: any[],
}

export interface ModuleMyPilotAssignmentsHandle {
    onRefresh: () => void;
}

interface ModuleMyPilotAssignmentsProps {
    ref: Ref<ModuleMyPilotAssignmentsHandle>,
    module: ModuleProps,
    onClick: (module: ModuleProps) => void,
}

export const ModuleMyPilotAssignments = ({
    ref,
    module,
    onClick
}: ModuleMyPilotAssignmentsProps) => {
        useImperativeHandle(ref, () => ({
            onRefresh() {
                setDataPromise(fetchData());
            }
        }));

        const [dataPromise, setDataPromise] = useState<null | Promise<PromiseProps>>(null);

        const fetchOnline = async () => {
            const response = await PilotApiRepository.getAssignedPilotagesAsync();
            const data = await response.json();

            for (const assignment of data) {
                const idbAssignment = await PilotAssignmentIdbRepository.getByPilotageIdAsync(Number(assignment.pilotage.pilotageId));
                if (idbAssignment) {
                    assignment.commands = idbAssignment.commands ?? [];
                } else {
                    assignment.commands = [];
                }
            }

            return {
                assignments: data
            }
        }

        const fetchOffline = async () => {
            const pilotAssignments = await PilotAssignmentIdbRepository.getAllActualAsync();
            return {
                assignments: pilotAssignments
            }
        }

        const fetchData = (): Promise<PromiseProps> => {
            if (navigator.onLine) {
                return fetchOnline();
            } else {
                return fetchOffline();
            }
        }

        useEffect(() => {
            setDataPromise(fetchData());
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);


        return (
            <ModuleWithData
                module={module}
                dataPromise={dataPromise}
                errorText="Kunne ikke hente aktuelle oppdrag.">
                <ModuleMyPilotAssignmentsContent
                    dataPromise={dataPromise}
                    module={module}
                    onClick={onClick} />
            </ModuleWithData>
        )
    }

interface ModuleMyPilotAssignmentsContentProps {
    dataPromise: Promise<PromiseProps>,
    module: ModuleProps,
    onClick: (module: ModuleProps) => void,
}
const ModuleMyPilotAssignmentsContent = ({
    dataPromise,
    module,
    onClick,
}: ModuleMyPilotAssignmentsContentProps) => {

    const data = use<PromiseProps>(dataPromise);

    return (
        <ModuleOnline module={module}>
            <div className={styles.fullModule}>
                {
                    data &&
                        (data.assignments.length === 0) ?
                        <ModuleDefault module={module} onClick={onClick} />
                        :
                        <CarouselContent data={data} module={module} />
                }
            </div>
        </ModuleOnline>
    )
}

interface CarouselContentProps {
    data: PromiseProps,
    module: ModuleProps,
}
const CarouselContent = ({ data, module }: CarouselContentProps) => {

    const navigate = useNavigate();

    const contentRef = useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [leftArrowVisible, setLeftArrowVisible] = useState(false);
    const [leftArrowFade, setLeftArrowFade] = useState<'in' | 'out'>('in');

    const [showRightArrow, setShowRightArrow] = useState(true);
    const [rightArrowVisible, setRightArrowVisible] = useState(true);
    const [rightArrowFade, setRightArrowFade] = useState<'in' | 'out'>('in');

    useEffect(() => {

        const handleScroll = () => {
            if (contentRef.current) {
                const { scrollLeft, scrollWidth, clientWidth } = contentRef.current;
                setShowLeftArrow(scrollLeft > 0);
                setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1); // -1 for floating point precision
            }
        };
        const carousel = contentRef.current;
        if (carousel) {
            carousel.addEventListener('scroll', handleScroll);
            // Initial check
            handleScroll();
        }

        return () => {
            if (carousel) {
                carousel.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    // Fade logic for left arrow
    useEffect(() => {
        if (showLeftArrow) {
            setLeftArrowVisible(true);
            setLeftArrowFade('in');
        } else if (leftArrowVisible) {
            setLeftArrowFade('out');
            const timeout = setTimeout(() => setLeftArrowVisible(false), 300);
            return () => clearTimeout(timeout);
        }
    }, [showLeftArrow, leftArrowVisible]);

    // Fade logic for right arrow
    useEffect(() => {
        if (showRightArrow) {
            setRightArrowVisible(true);
            setRightArrowFade('in');
        } else if (rightArrowVisible) {
            setRightArrowFade('out');
            const timeout = setTimeout(() => setRightArrowVisible(false), 300);
            return () => clearTimeout(timeout);
        }
    }, [showRightArrow, rightArrowVisible]);

    const onScrollLeft = () => {
    if (!contentRef.current) return;

    const { scrollLeft } = contentRef.current;
    const scrollAmount = Math.min(200, scrollLeft);
    contentRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
}

    const onScrollRight = () => {
        if (!contentRef.current) return;

        const { scrollLeft, scrollWidth, clientWidth } = contentRef.current;
        const maxScroll = scrollWidth - clientWidth - scrollLeft;
        const scrollAmount = Math.min(200, maxScroll);
        contentRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
    
    return (
        <>
            <div className={styles.title} onClick={() => navigate(createPilotPilotagesUrl())}>
                <div>
                    {module.text}
                </div>
                <div>
                    <IconPicker icon={module.icon} size={32} />
                </div>
            </div>
            <div
                className={`${styles.carousel}`}>

                {leftArrowVisible && (
                    <div
                        onClick={onScrollLeft}
                        className={`${styles.arrowLeft} ${leftArrowFade === 'in' ? styles.arrowFadeIn : styles.arrowFadeOut}`}>
                        <IconPicker icon="arrow-circle-left" size={32} color="var(--black)" />
                    </div>
                )}

                <div className={styles.carouselContent} ref={contentRef}>
                    {
                        data.assignments.map((assignment, index) => (
                            <CarouselContentArticle
                                key={index}
                                assignment={assignment}
                            />
                        ))
                    }
                </div>

                {rightArrowVisible && (
                    <div
                        className={`${styles.arrowRight} ${rightArrowFade === 'in' ? styles.arrowFadeIn : styles.arrowFadeOut}`}
                        onClick={onScrollRight}>
                        <IconPicker icon="arrow-circle-right" size={32} color="var(--black)" />
                    </div>
                )}

            </div>
        </>
    )
}

interface CarouselContentArticleProps {
    assignment: any,
}
const CarouselContentArticle = ({
    assignment
}: CarouselContentArticleProps) => {

    const navigate = useNavigate();

    const darkmode = useStoreSelector(AppStore, (state) => state.darkmode);

    const getFontColor = () => {
        if (!darkmode) {
            if (
                isPilotAssignmentTentative(assignment) ||
                assignment.pilotagePilotStatus.systemName === 'NOT_PROCESSED'
            ) {
                return "var(--font-secondary)";
            }
            if (assignment.pilotagePilotStatus.systemName === 'CORRECTION_NEEDED') {
                return "var(--white)";
            }
        }
        if (assignment.pilotagePilotStatus.systemName === 'CONFIRMED') {
            return "var(--pp-status-confirmed-color)";
        }
        return "var(--font-primary)";
    }

    const [fontColor, setFontColor] = useState(getFontColor());

    const showLocked = () => {
        return !isPilotAssignmentCompleted(assignment) && assignment.pilotage.pilotageDetail.isLocked;
    }

    useEffect(() => {
        setFontColor(getFontColor());
    }, [darkmode]);
    
    return (
        <article
            onClick={() => navigate(getPilotAssignmentUrl(assignment))}
            className={`${styles.carouselCard} ${styles.card}`}
            style={{ backgroundColor: getPilotAssignmentStatusColor(assignment), color: fontColor }}>
            <div className={styles.cardContent}>
                <div className={styles.cardContentTitle}>
                    <div className={`${styles.cardContentTitleText} text-truncate`}>
                        {assignment.pilotage.pilotageShip.shipName}
                    </div>
                    <div>
                        {
                            (showLocked()) &&
                                <IconPicker icon="lock" size={16} color={fontColor} />
                        }
                        {
                            (!isArrayEmpty(assignment.commands)) &&
                                <IconPicker icon="asterisk" size={16} color={fontColor} />
                        }
                    </div>
                </div>
                <div className={styles.cardContentText}>
                    <div>{moment(assignment.pilotage.pilotageDetail.fromTime).format('DD.MM HH:mm')}</div>
                    <div className="text-truncate">{assignment.pilotage.fromLocation.name}</div>
                    <div className={styles.cardToLocation}>
                        <div> <IconPicker icon="arrow-elbow-down-right" size={20} color={fontColor} /> </div>
                        <div className="text-truncate">{assignment.pilotage.toLocation.name}</div>
                    </div>
                </div>
            </div>

        </article>
    )
}
